/**
 * This component reads the logoImgRef, usually an <img /> in LogoFilePreview,
 * to generate a selectable color palette.
 */

import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import _ from 'lodash';
import ColorThief from 'colorthief';
import tinycolor from 'tinycolor2';

import { useBrand } from '../_libs/hooks/useBrand';
import { OrganizationBrandData } from '_libs/app/types';

const LogoColorSelector = ({
  logoImgRef,
}: {
  logoImgRef: React.MutableRefObject<HTMLImageElement>;
}): JSX.Element => {
  const [activeColor, setActiveColor] = useState<number | string>('rgb(0, 0, 0)');
  const [colorDivs, setColorDivs] = useState(null);
  const { register, setValue } = useFormContext();
  const {
    primaryBrand,
    setPrimaryBrand,
  } = useBrand();

  function handleColorClick (event: React.MouseEvent<HTMLButtonElement>) {
    const button = event.currentTarget;
    const color = button.dataset.color;

    // Set active color
    const activeColors = document.getElementsByClassName('is-active-color');
    _.map(activeColors, (activeColor) => {
      activeColor.classList.remove('is-active-color');
    });
    button.classList.add('is-active-color');
    setValue('color', color);
    setActiveColor(color);

    // Extract a tint and shade of the primary color
    const primary = (color as string);
    const hue = tinycolor(primary).toHsl().h;
    const light = `hsl(${hue}, 100%, 97%)`;
    const dark = `hsl(${hue}, 100%, 8%)`;

    // Update the primaryBrand's colors
    const _primaryBrand: OrganizationBrandData = _.cloneDeep(primaryBrand);
    _primaryBrand.colors = {
      light: tinycolor(light).toHslString(), // Note: Is this conversion even necessary?
      primary: tinycolor(primary).toHslString(), // Note: Is this conversion even necessary?
      dark: tinycolor(dark).toHslString(), // Note: Is this conversion even necessary?
    };
    setPrimaryBrand(_primaryBrand);

    // Update the colors of the page
    document.body.style.backgroundColor = light;
    document.body.style.color = dark;
  }

  function handleImageChange (logoImgRef) {
    if (logoImgRef.current) {
      const logo = logoImgRef.current;

      // Set logo dimensions right away in case palette generation hangs
      setValue('logo_height', logo.height);
      setValue('logo_width', logo.width);

      // Get the color palette
      const colorThief = new ColorThief();
      const palette = colorThief.getPalette(logo, 10, 5);

      // Create the UI for the user to pick the colors from the color palette
      const colors = [];
      const rgbs = _.map(palette, (rgbArr) => {
        // NOTE: rgb strings must include spaces after comma to match `color`
        return `rgb(${rgbArr[0]}, ${rgbArr[1]}, ${rgbArr[2]})`;
      });

      // Add black as a default color
      rgbs.push('rgb(0, 0, 0)');
      setValue('color', 'rgb(0, 0, 0)');

      // Remove any duplicates that colorThief has suggested
      const uniqueRgbs = _.uniq(rgbs);

      // Create the front-end markup for buttons
      _.map(uniqueRgbs, (rgb, i) => {
        colors.push(
          <div
            key={i}
            style={{
              display: 'inline-block',
            }}>
            <button
              type="button"
              data-color={rgb}
              style={{
                backgroundColor: rgb,
                width: '2rem',
                height: '2rem',
              }}
              onClick={handleColorClick}
              className={(rgb === activeColor) ? 'palette-color palette-color-mini is-active-color' : 'palette-color palette-color-mini'}
            >
            </button>
            <div className="f-small f-mono t-center mt-0">{
              i === uniqueRgbs.length - 1
                ? 'Black'
                : i + 1
            }</div>
          </div>
        );
      });
      setColorDivs(colors);
    } else {
      console.error('No logoImgRef.');
    }
  }

  useEffect(() => {
    handleImageChange(logoImgRef);
  }, [logoImgRef]);

  return (
    <div>
      <p className="f-small f-bold">
        Choose a color
      </p>
      {
        logoImgRef.current
        &&
        <div>
          {colorDivs}
          <input type="hidden" {...register('color')} />
          <input type="hidden" {...register('logo_width')} />
          <input type="hidden" {...register('logo_height')} />
        </div>
      }
    </div>
  );
};

export default LogoColorSelector;
